/**
 * 本地打印服务客户端
 *
 * 与运行在 Mac 上的 print-server.js 通信
 * 前端发送 Base64 PDF → 本地服务执行 lp 命令 → 静默打印
 */

const PRINT_SERVICE_URL = 'http://localhost:13528';

export interface PrintServiceStatus {
    status: string;
    platform: string;
    defaultPrinter: string;
    defaultPageSize: string;
    defaultResolution: string;
    printers: string[];
}

export interface PrintOptions {
    printer?: string;
    pageSize?: string;
    resolution?: string;
    copies?: number;
    fitToPage?: boolean;
}

/**
 * 检查本地打印服务是否可用
 */
export const checkPrintService = async (): Promise<{
    ready: boolean;
    printers: string[];
    defaultPrinter: string;
}> => {
    try {
        const response = await fetch(`${PRINT_SERVICE_URL}/status`, {
            signal: AbortSignal.timeout(3000),
        });
        if (!response.ok) throw new Error('Service error');
        const data: PrintServiceStatus = await response.json();
        return {
            ready: data.status === 'running',
            printers: data.printers,
            defaultPrinter: data.defaultPrinter,
        };
    } catch {
        return { ready: false, printers: [], defaultPrinter: '' };
    }
};

/**
 * 调用本地打印服务，静默打印 Base64 PDF
 */
export const localPrintPdf = async (
    pdfBase64: string,
    options?: PrintOptions,
): Promise<void> => {
    const response = await fetch(`${PRINT_SERVICE_URL}/print`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(30000),
        body: JSON.stringify({
            pdfBase64,
            printer: options?.printer,
            pageSize: options?.pageSize,
            resolution: options?.resolution,
            copies: options?.copies || 1,
            fitToPage: options?.fitToPage !== undefined ? options.fitToPage : true,
        }),
    });

    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Print failed');
    }
};
